import { useCallback, useEffect, useRef, useState } from 'react';
import { useSettings } from '../../../../contexts/SettingsContext';

const SAVE_DELAY_MS = 600;

/**
 * Local state for one `simpleDashboard.<key>` map, written field-level via
 * updateSimpleDashboardField (mergeFields). Edits are debounced (`immediate` saves now),
 * a waiting edit is flushed on unmount, remote changes resync the value unless a local edit
 * is waiting, and a failed save shows the stored value again.
 */
export const useFieldLevelSetting = (key, parse, serialize, { onSuccess, onError, savedMessage }) => {
  const { settings, updateSimpleDashboardField } = useSettings();
  const remote = settings.simpleDashboard?.[key];
  const remoteRef = useRef(remote);
  remoteRef.current = remote;
  const [value, setValue] = useState(() => parse(remote));
  const pending = useRef(null);
  const timer = useRef(null);

  const remoteKey = JSON.stringify(remote ?? null);
  useEffect(() => {
    if (pending.current) return;
    setValue(parse(remoteRef.current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteKey]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    const current = pending.current;
    if (!current) return;
    try {
      await updateSimpleDashboardField(key, serialize(current));
      if (pending.current === current) pending.current = null;
      onSuccess?.(savedMessage);
    } catch (e) {
      console.error(`Error saving ${key}:`, e);
      if (pending.current === current) {
        pending.current = null;
        // Show what is actually stored, not the edit that failed to save.
        setValue(parse(remoteRef.current));
      }
      onError?.(e?.message || 'Error');
    }
  }, [key, parse, serialize, updateSimpleDashboardField, onSuccess, onError, savedMessage]);

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => { flushRef.current(); }, []);

  const change = useCallback((patch, immediate = false) => {
    setValue((prev) => {
      const next = { ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) };
      pending.current = next;
      return next;
    });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flushRef.current(), immediate ? 0 : SAVE_DELAY_MS);
  }, []);

  return [value, change];
};
